import { DATASETS } from '../lib/analyticalQuery'
import { COMPOSITE_OUTPUTS } from '../lib/compositeOutputs'

/**
 * DatasetCatalog.jsx
 *
 * Stage 5 — explains, before selection, what each Dataset/Composite
 * Output represents, its time basis, and EXACTLY which dimensions it
 * supports — including the real, audited gaps (e.g.
 * historical_comparison has no resource_type/indirect_category/
 * sub_code/resource_code, confirmed in the Discriminator Gap
 * investigation). No dataset's card claims a capability it doesn't
 * have — this catalog is generated from DATASETS/COMPOSITE_OUTPUTS
 * themselves, not a separately-maintained description that could drift
 * out of sync with what the Adapters actually support.
 */

const DATASET_META = {
  cost_performance: {
    businessMeaning: 'The current, live financial state of the entire cost structure — right now, at this moment.',
    timeBasis: 'Live / Current — a single point in time.',
    useFor: 'Actual vs Budget vs EAC vs Commitment, present-day status.',
  },
  historical_comparison: {
    businessMeaning: 'What the numbers WERE, frozen at the moment of a specific Forecast Revision commit.',
    timeBasis: 'One committed Snapshot — you choose which one.',
    useFor: 'Tracking how EAC/Budget evolved between specific Forecast Revisions.',
  },
  time_series: {
    businessMeaning: 'How the live financial state has evolved across every committed Forecast Revision.',
    timeBasis: 'Every available Forecast Snapshot date, automatically discovered.',
    useFor: 'EAC/Actual trend curves, S-Curve analysis.',
  },
  monthly_actual_cost: {
    businessMeaning: 'Real transaction cost, month by month, from every source (IPC/Payroll/Store/Petty Cash/Accrual), by actual cost date.',
    timeBasis: 'Calendar months, automatically discovered from real transactions — no date selection needed.',
    useFor: 'Monthly Actual Trend, cash-out analysis by month.',
  },
}

const COMPOSITE_META = {
  s_curve: {
    businessMeaning: 'Planned Budget, Actual Cumulative, and EAC Forecast plotted together over time.',
    timeBasis: 'Same as time_series — every available Forecast Snapshot date.',
    useFor: 'Classic S-Curve project performance visualization.',
  },
}

export default function DatasetCatalog({ selectedKey, selectedType, onSelect }) {
  const datasetKeys = Object.keys(DATASETS)
  const compositeKeys = Object.keys(COMPOSITE_OUTPUTS)

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 10 }}>
        Datasets
      </div>
      <div style={{ display: 'grid', gap: 8, marginBottom: 20 }}>
        {datasetKeys.map(key => (
          <DatasetCard
            key={key}
            datasetKey={key}
            def={DATASETS[key]}
            meta={DATASET_META[key]}
            selected={selectedType === 'dataset' && selectedKey === key}
            onClick={() => onSelect('dataset', key)}
          />
        ))}
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#9CA3AF', marginBottom: 10 }}>
        Composite Outputs
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {compositeKeys.map(key => (
          <CompositeCard
            key={key}
            compositeKey={key}
            def={COMPOSITE_OUTPUTS[key]}
            meta={COMPOSITE_META[key]}
            selected={selectedType === 'composite' && selectedKey === key}
            onClick={() => onSelect('composite', key)}
          />
        ))}
      </div>
    </div>
  )
}

function DatasetCard({ datasetKey, def, meta, selected, onClick }) {
  return (
    <div onClick={onClick} style={{
      padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
      border: selected ? '2px solid #1A7A4A' : '1px solid #E5E7EB',
      background: selected ? '#F0FDF4' : '#fff',
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0F1017', marginBottom: 4 }}>{def.label}</div>
      <div style={{ fontSize: 11.5, color: '#6B7280', marginBottom: 6 }}>{meta?.businessMeaning}</div>
      <div style={{ fontSize: 10.5, color: '#9CA3AF' }}>
        <strong>Time basis:</strong> {meta?.timeBasis}
      </div>
      <div style={{ fontSize: 10.5, color: '#9CA3AF', marginTop: 4 }}>
        <strong>Dimensions:</strong> {def.dimensions.join(', ')}
      </div>
      {datasetKey === 'historical_comparison' && (
        <div style={{ fontSize: 10.5, color: '#B45309', marginTop: 4 }}>
          ⚠ Cost Code only — Resource Type, Indirect Category, Sub Code, and Resource Code are not available for historical snapshots.
        </div>
      )}
    </div>
  )
}

function CompositeCard({ compositeKey, def, meta, selected, onClick }) {
  return (
    <div onClick={onClick} style={{
      padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
      border: selected ? '2px solid #1A7A4A' : '1px solid #E5E7EB',
      background: selected ? '#F0FDF4' : '#fff',
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0F1017', marginBottom: 4 }}>{def.label}</div>
      <div style={{ fontSize: 11.5, color: '#6B7280', marginBottom: 6 }}>{meta?.businessMeaning}</div>
      <div style={{ fontSize: 10.5, color: '#9CA3AF' }}>
        <strong>Time basis:</strong> {meta?.timeBasis}
      </div>
      <div style={{ fontSize: 10.5, color: '#9CA3AF', marginTop: 4 }}>
        <strong>Series:</strong> {def.fixedSeries.join(', ')} (fixed — no measure selection)
      </div>
    </div>
  )
}
